import { useQueryClient } from '@tanstack/react-query';
import { useSite } from '../../../../context/page-context';
import { useOptimisticMutation, type Variables } from '../../../../hooks/use-optimistic-mutation';
import { useAccountApi } from '../../hooks/use-settings-api';
import type { TeamApiResponse, TeamMemberApiResponse } from '../../types';
import { QK_ACCOUNT_TEAM } from './use-account-team';

export function useSwapOwner() {
  const { accountApi } = useAccountApi();
  const { teamId } = useSite();
  const queryClient = useQueryClient();

  async function swapOwner(userId: number) {
    await accountApi.team(teamId).swapOwner(userId);
  }

  return useOptimisticMutation<TeamApiResponse, Variables<typeof swapOwner>>({
    queryClient,
    queryKey: QK_ACCOUNT_TEAM(teamId),
    mutationFn: swapOwner,
    optimisticUpdate: (prev, userId) => {
      if (!prev) {
        return prev;
      }

      const newOwner = prev.users.find((user: TeamMemberApiResponse) => user.id === userId);
      if (!newOwner) {
        return prev;
      }

      const demotedOwner: TeamMemberApiResponse = {
        id: prev.owner.id,
        name: prev.owner.name,
        email: prev.owner.email,
        role: 'admin',
      };

      return {
        ...prev,
        owner: { id: newOwner.id, name: newOwner.name, email: newOwner.email },
        users: [
          ...prev.users.filter((user: TeamMemberApiResponse) => user.id !== userId),
          demotedOwner,
        ],
      };
    },
  });
}
